import { PROTOCOL_VERSION, type ServerEvent } from '@dijitalapp/shared';
import { api } from './api';

export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'offline';

type Listener = (event: ServerEvent) => void;

const CLOSE_SESSION_REVOKED = 4001;
const MAX_BACKOFF_MS = 30_000;

/**
 * WebSocket client for the realtime gateway (REALTIME_PROTOCOL.md): ticket handshake, heartbeat,
 * reconnect with exponential backoff + jitter, and a `connected` hook the store uses to run cursor
 * sync for anything missed while disconnected.
 */
export class RealtimeClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private stateListeners = new Set<(s: ConnectionState) => void>();
  private attempt = 0;
  private stopped = false;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private lastFrameAt = 0;
  state: ConnectionState = 'connecting';

  constructor(private readonly onSessionRevoked: () => void) {
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
  }

  onEvent(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  onState(l: (s: ConnectionState) => void): () => void {
    this.stateListeners.add(l);
    return () => this.stateListeners.delete(l);
  }

  private setState(s: ConnectionState) {
    this.state = s;
    this.stateListeners.forEach((l) => l(s));
  }

  async start(): Promise<void> {
    this.stopped = false;
    if (!navigator.onLine) {
      this.setState('offline');
      return;
    }
    this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');
    let ticket: string;
    try {
      ticket = (await api<{ ticket: string }>('POST', '/realtime/ticket')).ticket;
    } catch {
      this.scheduleReconnect();
      return;
    }
    if (this.stopped) return;
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${scheme}://${location.host}/api/v1/realtime/ws?ticket=${ticket}`);
    this.ws = ws;

    ws.onmessage = (msg) => {
      this.lastFrameAt = Date.now();
      let event: ServerEvent;
      try {
        event = JSON.parse(String(msg.data)) as ServerEvent;
      } catch {
        return;
      }
      if (event.event === 'hello') {
        this.attempt = 0;
        this.setState('connected');
        this.startHeartbeat(event.payload.heartbeatIntervalMs);
      }
      if (event.event === 'server.draining') {
        this.scheduleReconnect(event.payload.reconnectAfterMs);
      }
      // Unknown events are ignored by listeners (forward compatibility).
      this.listeners.forEach((l) => l(event));
    };

    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.stopHeartbeat();
      if (e.code === CLOSE_SESSION_REVOKED) {
        this.stopped = true;
        this.onSessionRevoked();
        return;
      }
      if (!this.stopped) this.scheduleReconnect();
    };
  }

  /** Fire-and-forget command (dropped while disconnected); `reqId` lets error frames be matched to it. */
  command(cmd: string, payload: Record<string, unknown>, reqId?: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ version: PROTOCOL_VERSION, cmd, ...(reqId ? { req_id: reqId } : {}), payload }));
    }
  }

  private startHeartbeat(intervalMs: number) {
    this.stopHeartbeat();
    this.lastFrameAt = Date.now();
    this.heartbeat = setInterval(() => {
      if (Date.now() - this.lastFrameAt > intervalMs * 2) {
        // Two silent intervals: the connection is dead even if the browser has not noticed.
        this.ws?.close();
        return;
      }
      this.ws?.send(JSON.stringify({ version: PROTOCOL_VERSION, cmd: 'ping' }));
    }, intervalMs);
  }

  private stopHeartbeat() {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  private scheduleReconnect(afterMs?: number) {
    if (this.stopped) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (!navigator.onLine) {
      this.setState('offline');
      return;
    }
    this.setState('reconnecting');
    this.attempt += 1;
    const base = Math.min(MAX_BACKOFF_MS, 500 * 2 ** Math.min(this.attempt, 6));
    const delay = afterMs ?? base / 2 + Math.random() * (base / 2);
    this.retryTimer = setTimeout(() => void this.start(), delay);
  }

  private onOnline = () => {
    if (!this.ws && !this.stopped) {
      this.attempt = 0;
      void this.start();
    }
  };

  private onOffline = () => {
    this.setState('offline');
    this.ws?.close();
  };

  stop(): void {
    this.stopped = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.stopHeartbeat();
    this.ws?.close();
    this.ws = null;
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
  }
}
